/**
 * Text box layout: wrapping, alignment and shrink-to-fit.
 *
 * Vertical metrics follow technical lettering rather than typesetting: a
 * line's visual top is its cap height (title-block values sit centered on
 * their capitals), and the box bottom is the last baseline (descenders may
 * hang below, like on a drawn plan).
 */

import type { HorizontalAlign, VerticalAlign } from 'shared-types';
import type { FontFace } from './fonts';

export interface TextBoxInput {
  text: string;
  face: FontFace;
  emMm: number;
  /** Line pitch as a multiple of the em. */
  lineHeight: number;
  letterSpacingMm: number;
  widthMm: number;
  heightMm: number;
  paddingMm: number;
  align: HorizontalAlign;
  valign: VerticalAlign;
  /** Shrink (down to `minEmMm`) instead of overflowing. */
  autoShrink: boolean;
  minEmMm: number;
}

export interface LaidOutLine {
  text: string;
  /** Baseline start, box-local millimeters. */
  x: number;
  y: number;
  widthMm: number;
}

export interface LaidOutText {
  emMm: number;
  lines: LaidOutLine[];
  /** True when the text still doesn't fit (too long even at the minimum size, or shrinking is off). */
  overflow: boolean;
}

export function measureLine(text: string, face: FontFace, emMm: number, letterSpacingMm: number): number {
  if (!text) return 0;
  // Word by word (each width shaped once and cached by the face): wrapping tries many lines made of the same words.
  const words = text.split(' ');
  let em = face.measure(' ') * (words.length - 1);
  for (const word of words) em += face.measure(word);
  return em * emMm + letterSpacingMm * Math.max(0, [...text].length - 1);
}

/** Splits a word wider than the box between characters (the last part stays open for more text). */
function breakWord(word: string, maxWidth: number, width: (s: string) => number): string[] {
  const parts: string[] = [];
  let chunk = '';
  for (const ch of [...word]) {
    if (chunk && width(chunk + ch) > maxWidth) {
      parts.push(chunk);
      chunk = ch;
    } else {
      chunk += ch;
    }
  }
  parts.push(chunk);
  return parts;
}

/** Greedy word wrap per paragraph (`\n`); words wider than the box are broken between characters. */
export function wrapLines(text: string, maxWidth: number, width: (s: string) => number): string[] {
  const out: string[] = [];
  for (const paragraph of text.split('\n')) {
    let line = '';
    for (const word of paragraph.split(' ').filter(Boolean)) {
      const candidate = line ? `${line} ${word}` : word;
      if (width(candidate) <= maxWidth) {
        line = candidate;
        continue;
      }
      if (line) out.push(line);
      const parts = width(word) <= maxWidth ? [word] : breakWord(word, maxWidth, width);
      out.push(...parts.slice(0, -1));
      line = parts[parts.length - 1];
    }
    out.push(line);
  }
  return out;
}

function layoutAt(input: TextBoxInput, emMm: number): LaidOutText {
  const { face, letterSpacingMm, paddingMm, align, valign } = input;
  const innerW = Math.max(0, input.widthMm - 2 * paddingMm);
  const innerH = Math.max(0, input.heightMm - 2 * paddingMm);
  const width = (s: string) => measureLine(s, face, emMm, letterSpacingMm);
  const lines = wrapLines(input.text, innerW, width);
  const pitch = input.lineHeight * emMm;
  const cap = face.capHeightRatio * emMm;
  const blockH = cap + pitch * (lines.length - 1);
  const top = valign === 'top' ? paddingMm : valign === 'middle' ? paddingMm + (innerH - blockH) / 2 : paddingMm + innerH - blockH;
  const laid = lines.map((text, i) => {
    const w = width(text);
    const x = align === 'left' ? paddingMm : align === 'center' ? paddingMm + (innerW - w) / 2 : paddingMm + innerW - w;
    return { text, x, y: top + cap + i * pitch, widthMm: w };
  });
  const tooWide = laid.some((line) => line.widthMm > innerW + 1e-6);
  // Descenders of the last line may hang into the padding; only the cap block must fit.
  const tooTall = blockH > innerH + 1e-6;
  // A wrapped word broken mid-word also counts as not fitting (only when shrinking can help).
  const brokeWord = input.text.split(/\s+/).some((word) => word && width(word) > innerW + 1e-6);
  return { emMm, lines: laid, overflow: tooWide || tooTall || brokeWord };
}

/** Auto-shrink settles within this (em mm): a few tries instead of many small steps. */
const SHRINK_PRECISION_MM = 0.02;

export function layoutTextBox(input: TextBoxInput): LaidOutText {
  const full = layoutAt(input, input.emMm);
  if (!input.autoShrink || !full.overflow) return full;
  // The largest size that fits, by halving the range (sizes that fit form a range below the overflowing one).
  const smallest = layoutAt(input, input.minEmMm);
  if (smallest.overflow) return smallest;
  let fits = smallest;
  let low = input.minEmMm;
  let high = input.emMm;
  while (high - low > SHRINK_PRECISION_MM) {
    const mid = (low + high) / 2;
    const tried = layoutAt(input, mid);
    if (tried.overflow) high = mid;
    else {
      fits = tried;
      low = mid;
    }
  }
  return fits;
}
